<script lang="ts">
	/**
	 * The downloads available to the signed-in user for one product.
	 *
	 * It sits above the licence list because it is what people actually came for:
	 * a licence is the reason you may have the build, the build is the thing you
	 * want. The entitlement decision is the licensing backend's — this renders what
	 * it already agreed the caller may have, and every link is re-checked and
	 * minted there.
	 *
	 * Each build is a card, and the whole card is the action — a download has one
	 * obvious thing you want to do with it, so a separate button inside a clickable
	 * tile would only add a smaller target for the same outcome. The card is a real
	 * `role="button"` with keyboard handling rather than a div with a click, and the
	 * version picker stops propagation so choosing a version is not a download.
	 *
	 * A product with a web application configured (Settings → Admin → Licensing)
	 * gets one more card, always first: it opens the web app in a new tab instead
	 * of fetching a file, and it is shown whatever the backend lists — including
	 * nothing, since using the product in the browser needs no build. A product
	 * without one shows no such card.
	 */

	import { Badge, Spinner } from 'flowbite-svelte';
	import SelectField from './store/SelectField.svelte';
	import EmptyState from './EmptyState.svelte';
	import { listLicenseDownloads, getLicenseDownloadURL } from '../AppNetwork';
	import { sanitizeHTML } from '../sanitizeHTML';
	import { toastError } from '../toast';
	import type { ApplicationDownloadGroup, ApplicationWebApplication, LicenseDownload, LicenseDownloadVersion } from '../AppTypes';
	import { t, t2 } from '../i18n';
	import ListRowsPlaceholder from './store/placeholders/ListRowsPlaceholder.svelte';

	interface Props {
		/** Which product's downloads to show. */
		appUUID: string;
		/** Display names for the backend's group identifiers, in the order to show them. */
		groups?: ApplicationDownloadGroup[];
		/** The product's web application, shown as the first card, or `null` for none. */
		webApplication?: ApplicationWebApplication | null;
	}

	let { appUUID, groups = [], webApplication = null }: Props = $props();

	/** Files the caller may download. */
	let downloads = $state<LicenseDownload[]>([]);
	/** Whether the listing is loading. */
	let loading = $state(true);
	/** Failure of the listing. */
	let error = $state('');
	/** Version chosen per file, keyed by file UUID. Absent means the current release. */
	let chosenVersion = $state<Record<string, string>>({});
	/** File currently being fetched a link for, or `null`. */
	let fetching = $state<string | null>(null);

	/** Load the downloads for the selected product. */
	async function load(uuid: string): Promise<void> {
		loading = true;
		error = '';
		downloads = [];
		/** Fetch the entitled files; an empty list is a normal outcome. */
		try {
			const page = await listLicenseDownloads(uuid);
			downloads = page.items;
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load downloads');
		} finally {
			loading = false;
		}
	}

	/* Reload when the product changes — this panel follows the picker above it. */
	$effect(() => {
		load(appUUID);
	});

	/**
	 * Fetches a signed link and hands it to the browser.
	 *
	 * Assigning `location` rather than opening a tab: the link responds with a file,
	 * so a new tab would flash empty and close.
	 */
	async function download(file: LicenseDownload): Promise<void> {
		/* Guard the action rather than only dimming the card: a card with nothing
		   published has no link to fetch, and a second click while one is in flight
		   would mint a second link and record a second download. */
		if (!activeVersion(file) || fetching) return;

		fetching = file.fileRecordUUID;
		/** Mint the link, then navigate to it so the browser saves the file. */
		try {
			const url = await getLicenseDownloadURL(appUUID, file.fileRecordUUID, chosenVersion[file.fileRecordUUID]);
			window.location.href = url;
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to start the download'));
		} finally {
			fetching = null;
		}
	}

	/**
	 * The mapping key that names the bucket for builds the backend filed under no
	 * group at all.
	 *
	 * The empty string can never collide with a real identifier, because a blank
	 * group is exactly what puts a build in this bucket in the first place. That
	 * makes it a reserved key an administrator can name like any other, without a
	 * separate column to hold the one exception.
	 */
	const UNGROUPED_KEY = '';

	/** One heading's worth of builds. */
	interface DownloadGroup {
		/** The backend's group identifier, or {@link UNGROUPED_KEY} for builds with none. */
		key: string;
		/** Heading to show. */
		name: string;
		/** Builds in the group. */
		files: LicenseDownload[];
	}

	/**
	 * The builds, grouped by the identifier the backend files them under.
	 *
	 * Configured groups come first and in their configured order — that ordering is
	 * the reason the mapping is a list rather than an object. Groups the backend
	 * reports that nobody has named follow alphabetically under their raw
	 * identifier: an unnamed group is a labelling gap, not a reason to hide a
	 * build. Anything filed under no group at all lands in a bucket of its own,
	 * last, called "Other" unless an administrator has named it something better.
	 */
	let grouped = $derived.by(() => {
		/* Plain objects rather than `Map`: this is a local computation inside a
		   derived, not reactive state, and a `Map` here trips the rule that guards
		   against un-reactive collections held in `$state`. */
		const byKey: Record<string, LicenseDownload[]> = {};
		const ungrouped: LicenseDownload[] = [];

		/* Bucket every build by the identifier the backend filed it under. */
		for (const file of downloads) {
			const key = file.group?.trim();
			/* No group is its own bucket, not a group literally called "". */
			if (!key) {
				ungrouped.push(file);
				continue;
			}
			(byKey[key] ??= []).push(file);
		}

		const named: Record<string, string> = {};
		/* Later duplicates lose, which matches how the editor reads top to bottom. */
		for (const group of groups) named[group.key] ??= group.name;

		/* The reserved key names the trailing bucket, so it is not a group in its
		   own right here — filtering by `byKey` already excludes it, since no build
		   can carry a blank group and still be in `byKey`. */
		const configured = groups.map((group) => group.key).filter((key) => key in byKey);
		const remaining = Object.keys(byKey)
			.filter((key) => !(key in named))
			.sort((left, right) => left.localeCompare(right));

		const result: DownloadGroup[] = [...configured, ...remaining].map((key) => ({
			key,
			name: named[key] ?? key,
			files: byKey[key]
		}));

		/* Builds under no group at all trail the named ones, under their own
		   heading — nameable like any other group, defaulted when it is not. */
		if (ungrouped.length > 0) {
			result.push({
				key: UNGROUPED_KEY,
				name: named[UNGROUPED_KEY]?.trim() || $t('Other'),
				files: ungrouped
			});
		}
		return result;
	});

	/**
	 * Whether to draw group headings.
	 *
	 * A single heading above every build names nothing the reader did not already
	 * know, so headings appear only once there is more than one group to tell apart.
	 */
	let showHeadings = $derived(grouped.length > 1);

	/** Formats a byte count, or a dash when the backend reported none. */
	function formatSize(bytes: number | null): string {
		if (bytes === null || bytes <= 0) return '—';
		const units = ['B', 'KB', 'MB', 'GB', 'TB'];
		let value = bytes;
		let unit = 0;
		/* Step up a unit at a time rather than computing a log, so the last unit
		   caps the scale instead of producing an index past the end of the array. */
		while (value >= 1024 && unit < units.length - 1) {
			value /= 1024;
			unit++;
		}
		return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
	}

	/** Formats a release date, or a dash when there is none. */
	function formatDate(date: string | null): string {
		if (!date) return '—';
		return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
	}

	/**
	 * Icons whose URL failed to load, by file identifier.
	 *
	 * An uploaded icon can outlive the object it points at. Without this the card
	 * shows a broken-image box, which is worse than the glyph it replaced.
	 */
	let brokenIcons = $state<Record<string, true>>({});

	/** Font Awesome glyph for a platform, falling back to a generic file. */
	function platformIcon(platform: string | null): string {
		const key = (platform || '').toLowerCase();
		if (key.includes('win')) return 'fa-brands fa-windows';
		if (key.includes('mac') || key.includes('osx') || key.includes('darwin')) return 'fa-brands fa-apple';
		if (key.includes('linux')) return 'fa-brands fa-linux';
		return 'fa-solid fa-file-arrow-down';
	}

	/**
	 * Activates a card from the keyboard.
	 *
	 * Enter and Space are what `role="button"` promises, so both are honoured; the
	 * default for Space is suppressed because on a focusable element it scrolls.
	 */
	function handleCardKey(event: KeyboardEvent, file: LicenseDownload): void {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		download(file);
	}

	/**
	 * The second line under a version in the picker: when it was released and how large it is,
	 * led by a note on the release the plain download serves, so the reader can tell an older
	 * build from the current one before choosing it.
	 */
	function versionDescription(version: LicenseDownloadVersion): string {
		const facts = `${$t('Released')} ${formatDate(version.releasedAt)} · ${formatSize(version.size)}`;
		return version.isActive ? `${$t('Current release')} · ${facts}` : facts;
	}

	/** The version a card's action currently refers to. */
	function activeVersion(file: LicenseDownload) {
		const chosen = chosenVersion[file.fileRecordUUID];
		return (chosen ? file.versions.find((version) => version.versionID === chosen) : null) ?? file.latest;
	}
</script>

{#if webApplication}
	<!-- The web application, first: a real anchor, since it navigates rather than
	     downloads, so middle-click and copy-link work as they do on any link. -->
	<div class="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
		<a
			href={webApplication.url}
			target="_blank"
			rel="noopener noreferrer"
			data-testid="web-application"
			class="widget bg-depth-hover group focus-visible:ring-primary-500 flex h-full cursor-pointer flex-col gap-3 !p-4 text-left transition hover:shadow-md focus-visible:ring-2 focus-visible:outline-none"
		>
			<div class="flex items-start gap-3">
				<div class="bg-primary-100 dark:bg-primary-900/30 shrink-0 rounded-lg p-3">
					<i class="fa-solid fa-globe text-primary-600 dark:text-primary-400 text-lg"></i>
				</div>
				<div class="min-w-0 flex-1">
					<p class="truncate font-medium text-gray-900 dark:text-white">{webApplication.name}</p>
					<div class="mt-1 flex flex-wrap items-center gap-1">
						<Badge color="gray">{$t('Web')}</Badge>
					</div>
				</div>
				<span class="group-hover:text-primary-500 text-gray-300 transition dark:text-gray-600">
					<i class="fa-solid fa-arrow-up-right-from-square"></i>
				</span>
			</div>
			{#if webApplication.description}
				<p class="line-clamp-3 text-xs text-gray-500 dark:text-gray-400">{webApplication.description}</p>
			{/if}
			<p class="mt-auto text-xs text-gray-400 dark:text-gray-500">{$t('Opens in your browser — nothing to install.')}</p>
		</a>
	</div>
{/if}

{#if loading}
	<ListRowsPlaceholder rows={3} />
{:else if error}
	<EmptyState variant="error" description={error} compact />
{:else if downloads.length === 0}
	{#if !webApplication}
	<!-- The backend offers a file only when an entitlement on one of your in-date
	     licences is linked to it, so an empty list is a real answer, not a failure.
	     Saying which makes the difference between "nothing for me yet" and "this
	     panel is broken" — which rendering nothing at all would not. -->
	<EmptyState
		faIcon="download"
		title={$t('No downloads available')}
		description={$t('Builds appear here once they are linked to an entitlement on one of your active licenses.')}
		compact
	/>
	{/if}
{:else}
	<div class="space-y-6">
		{#each grouped as group (group.key)}
			<div>
				{#if showHeadings}
					<h4 class="card-label mb-2">{group.name}</h4>
				{/if}

				<div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{#each group.files as file (file.fileRecordUUID)}
						{@const version = activeVersion(file)}
						{@const busy = fetching === file.fileRecordUUID}
						<div
							role="button"
							tabindex={version ? 0 : -1}
							aria-disabled={!version || busy}
							aria-label={$t('Download {{name}}', { name: file.name })}
							class="widget bg-depth-hover group focus-visible:ring-primary-500 flex h-full flex-col gap-3 !p-4 text-left
					transition focus-visible:ring-2 focus-visible:outline-none
					{version && !busy ? 'cursor-pointer hover:shadow-md' : 'cursor-not-allowed opacity-60'}"
							onclick={() => download(file)}
							onkeydown={(event) => handleCardKey(event, file)}
						>
							<div class="flex items-start gap-3">
								<div class="bg-primary-100 dark:bg-primary-900/30 shrink-0 rounded-lg p-3">
									<!-- The build's own icon wins, because it is the one somebody chose
									     for it. The platform glyph is the fallback, and the generic file
									     glyph the fallback for that — so a build always carries a mark,
									     whatever is configured. -->
									{#if file.icon && !brokenIcons[file.fileRecordUUID]}
										<img
											src={file.icon}
											alt=""
											class="h-[1.125rem] w-[1.125rem] object-contain"
											onerror={() => (brokenIcons = { ...brokenIcons, [file.fileRecordUUID]: true })}
										/>
									{:else}
										<i
											class="{platformIcon(
												file.platform
											)} text-primary-600 dark:text-primary-400 text-lg"
										></i>
									{/if}
								</div>

								<div class="min-w-0 flex-1">
									<p class="truncate font-medium text-gray-900 dark:text-white">{file.name}</p>
									<div class="mt-1 flex flex-wrap items-center gap-1">
										{#if version?.versionName}
											<Badge color="blue">{version.versionName}</Badge>
										{/if}
										{#if file.platform}
											<Badge color="gray">{file.platform}</Badge>
										{/if}
									</div>
								</div>

								<!-- The affordance, not the control: it says the card is clickable and
					     doubles as the progress indicator once it has been clicked. -->
								<span class="group-hover:text-primary-500 text-gray-300 transition dark:text-gray-600">
									{#if busy}
										<Spinner size="4" />
									{:else}
										<i class="fa-solid fa-download"></i>
									{/if}
								</span>
							</div>

							{#if file.description}
								<!-- `sanitizeHTML` is an allow-list run over the parsed tree, so this
					     markup is safe to render; the rule cannot see through the call,
					     and printing it as text is what put raw `<p>` tags on the page. -->
								<div
									class="richtext line-clamp-3 text-xs text-gray-500 dark:text-gray-400 [&_p]:m-0 [&_p+p]:mt-1"
								>
									<!-- eslint-disable-next-line svelte/no-at-html-tags -->
									{@html sanitizeHTML(file.description)}
								</div>
							{/if}

							<!-- Pushed to the foot so cards in a row line their metadata up even when
				     their descriptions differ in length. -->
							<p class="mt-auto text-xs text-gray-400 dark:text-gray-500">
								{formatSize(version?.size ?? null)} · {$t('Released')}
								{formatDate(version?.releasedAt ?? null)}
							</p>

							<!-- Only files with more than one release get a picker; one release is
				     not a choice. Its events stop here — choosing a version is not a
				     request to download one. -->
							{#if file.isVersioned}
								<div
									onclick={(event) => event.stopPropagation()}
									onkeydown={(event) => event.stopPropagation()}
									role="none"
								>
									<!-- One-way value plus a reassigning onchange: a record entry that
									     was never chosen is absent, and binding it is the shape
									     CLAUDE.md warns about. -->
									<SelectField
										class="w-full"
										value={chosenVersion[file.fileRecordUUID] ?? ''}
										aria-label={$t('Version')}
										items={file.versions.map((entry) => ({
											value: entry.versionID,
											name: entry.versionName || entry.versionID,
											description: versionDescription(entry)
										}))}
										placeholder={$t('Latest')}
										onchange={({ value }) =>
											(chosenVersion = { ...chosenVersion, [file.fileRecordUUID]: value })}
									/>
								</div>
							{/if}
						</div>
					{/each}
				</div>
			</div>
		{/each}
	</div>
{/if}
